/**
 * Hook to fetch user's pooled market supply positions (legacy Aave-style pools).
 * These are positions where the user has supplied liquidity to pooled lending markets.
 */

import { useMemo } from 'react'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import type { UserPosition, Reserve } from '@/domain/market-info/marketInfo'
import type { Address } from 'viem'

export interface PooledMarketSupply {
  /** Underlying token that was supplied */
  token: Token
  /** aToken representing the supply position */
  aToken: Token
  /** Total amount of underlying supplied (collateral balance) */
  balance: NormalizedUnitNumber
  /** Available balance to withdraw (min of collateral balance and pool liquidity) */
  availableBalance: NormalizedUnitNumber
  /** Value in USD */
  balanceUSD: NormalizedUnitNumber
  /** Available balance value in USD */
  availableBalanceUSD: NormalizedUnitNumber
  /** Current supply APY */
  supplyAPY: number
  /** The reserve data */
  reserve: Reserve
  /** Raw user position */
  position: UserPosition
}

export interface UsePooledMarketSuppliesParams {
  /** Chain ID for the pooled market */
  chainId?: number
}

export interface UsePooledMarketSuppliesResult {
  /** List of user's pooled market supply positions with balance > 0 */
  supplies: PooledMarketSupply[]
  /** Whether data is loading */
  isLoading: boolean
  /** Any error */
  error: Error | null
}

// Default chain ID for pooled markets (HyperLiquid)
const DEFAULT_POOLED_CHAIN_ID = 999

/**
 * Fetch user's pooled market supply positions
 * Returns only positions where user has positive collateral balance
 */
export function usePooledMarketSupplies({ 
  chainId = DEFAULT_POOLED_CHAIN_ID 
}: UsePooledMarketSuppliesParams = {}): UsePooledMarketSuppliesResult {
  const { marketInfo, isLoading, error } = useMarketInfo({ chainId })

  const supplies = useMemo((): PooledMarketSupply[] => {
    if (!marketInfo?.userPositions) return []

    return marketInfo.userPositions
      .filter((position) => {
        // Only include positions with positive collateral balance
        return position.collateralBalance.gt(0)
      })
      .map((position) => {
        const { reserve } = position
        
        // Available balance is the minimum of user's collateral and pool's available liquidity
        // This is how much the user can actually withdraw
        const availableLiquidity = reserve.availableLiquidity
        const availableBalance = position.collateralBalance.lt(availableLiquidity)
          ? position.collateralBalance
          : availableLiquidity

        return {
          token: reserve.token,
          aToken: reserve.aToken,
          balance: position.collateralBalance,
          availableBalance: NormalizedUnitNumber(availableBalance.toString()),
          balanceUSD: NormalizedUnitNumber(
            position.collateralBalance.multipliedBy(reserve.priceInUSD).toString()
          ),
          availableBalanceUSD: NormalizedUnitNumber(
            availableBalance.multipliedBy(reserve.priceInUSD).toString()
          ),
          supplyAPY: (reserve.supplyAPY?.toNumber() ?? 0) * 100, // Convert from decimal to percentage
          reserve,
          position,
        }
      })
  }, [marketInfo?.userPositions])

  return {
    supplies,
    isLoading,
    error: error instanceof Error ? error : null,
  }
}

/**
 * Utility to convert a PooledMarketSupply to a swap-compatible format
 */
export function pooledSupplyToSwapOption(supply: PooledMarketSupply): {
  type: 'pooled'
  token: Token
  balance: NormalizedUnitNumber
  aTokenAddress: Address
  underlyingAddress: Address
} {
  return {
    type: 'pooled' as const,
    token: supply.token,
    balance: supply.balance,
    aTokenAddress: supply.aToken.address as Address,
    underlyingAddress: supply.token.address as Address,
  }
}

